import React from 'react';
import get from 'lodash/get';
import { PapiEditorText } from '@wix/da-papi-types';
import LegacyTextRenderer, { type LegacyTheme } from './LegacyTextRenderer';
import ExcerptRenderer from './ExcerptRenderer';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import type { ExternalReactRenderers } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import DeviationMediaService from '../utils/DeviationMediaService';
import MonoView from '../DaEditorNg/MonoView';
import GalleryView from '../DaEditorNg/GalleryView';
import EmoteSelectorNg from '../EmoteSelectorNg';
import {
  Dims,
  MeasuredCookieType,
} from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { useSafeUrlHandler } from '../SafeUrlModal/hooks/useSafeUrlHandler';
import InlineView from '../DaEditorNg/InlineView';
import s from './LegacyTextRenderer.scss';

export const externalRenderers: ExternalReactRenderers = {
  deviation: MonoView,
  gallery: GalleryView,
  mediaService: DeviationMediaService,
  emoteSelector: EmoteSelectorNg,
  inline: InlineView,
};

export interface Props {
  textContent?: PapiEditorText;
  className?: string;
  fallbackToExcerpt?: boolean;
  emptyPlaceHolder?: string;
  legacyTheme?: LegacyTheme;
  // You can use this pretty freely as backend will send sanitisied data (from DAML)
  htmlExcerptLegacy?: boolean;
  // Polls are special and have html in their excerpt
  isPollWithHtmlExcerpt?: boolean;
  excerptOnly?: boolean;
  viewerKey?: string | number;
  disableStylesForLegacy?: boolean; // We want to disable eclipse styles for certain legacy content to avoid conflicting with legacy css
  // external domains get a warning modal
  withSafetyCheckModal?: boolean;
  // New tiptap options
  tipTapViewer?: any;
  tipTapPresetId?: string; // If you want to overide the preset id
  cookieType?: MeasuredCookieType | undefined;
  defaultSize?: {
    desktop: Dims;
    mobile: Dims;
  };
}

const TextRenderer = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      textContent,
      className,
      fallbackToExcerpt = true,
      htmlExcerptLegacy,
      isPollWithHtmlExcerpt,
      legacyTheme,
      excerptOnly,
      viewerKey,
      disableStylesForLegacy,
      emptyPlaceHolder = '',
      withSafetyCheckModal,
      tipTapViewer,
      tipTapPresetId,
      cookieType,
      defaultSize,
    },
    ref
  ) => {
    const safeHandler = useSafeUrlHandler();

    if (!textContent) {
      return null;
    }

    const type = get(textContent, 'html.type');

    const markup = get(textContent, 'html.markup');

    if (!markup || excerptOnly) {
      if (!fallbackToExcerpt) {
        return null;
      }
      return (
        <ErrorBoundary debugComponent="TextRendererExcerpt">
          <ExcerptRenderer
            textContent={textContent}
            className={className}
            emptyPlaceHolder={emptyPlaceHolder}
            legacyTheme={legacyTheme}
            htmlExcerptLegacy={htmlExcerptLegacy}
            isPollWithHtmlExcerpt={isPollWithHtmlExcerpt}
            disableStylesForLegacy={disableStylesForLegacy}
            ref={ref}
          />
        </ErrorBoundary>
      );
    }

    switch (type) {
      case 'writer':
        return (
          <ErrorBoundary debugComponent="TextRendererWriter">
            <div ref={ref} className={s['legacy-container']}>
              <LegacyTextRenderer
                className={className}
                html={markup}
                theme={legacyTheme}
                disableStyles={disableStylesForLegacy}
                withSafetyCheckModal={withSafetyCheckModal}
              />
            </div>
          </ErrorBoundary>
        );
      case 'tiptap':
        const ViewerTipTap = tipTapViewer;
        if (!tipTapViewer) {
          return null;
        }
        try {
          if (withSafetyCheckModal) {
            return (
              <ErrorBoundary debugComponent="TextRendererTipTap">
                <div onClickCapture={safeHandler}>
                  <ViewerTipTap
                    key={viewerKey}
                    contentJson={JSON.parse(markup)}
                    className={className}
                    externalRenderers={externalRenderers}
                    cookieType={cookieType}
                    defaultSize={defaultSize}
                    ref={ref}
                    presetId={tipTapPresetId}
                  />
                </div>
              </ErrorBoundary>
            );
          }
          return (
            <ErrorBoundary debugComponent="TextRendererTipTap">
              <ViewerTipTap
                contentJson={JSON.parse(markup)}
                className={className}
                externalRenderers={externalRenderers}
                cookieType={cookieType}
                defaultSize={defaultSize}
                ref={ref}
                presetId={tipTapPresetId}
              />
            </ErrorBoundary>
          );
        } catch (e) {
          return null;
        }
      default:
        return null;
    }
  }
);
TextRenderer.displayName = 'TextRenderer';

export default TextRenderer;
